import React, { useCallback, useState } from 'react';
import { Coins, Globe2, Languages, Monitor, MonitorSmartphone, Store } from 'lucide-react';

import { loadStorefrontView } from './api';
import { Empty, WorkspaceTabs } from './parts';
import { countryName } from './countries';
import { useLiveResource } from './useLive';
import { Flag, HourHeatmap, KpiRow, NUMBER, Panel, PulseMap } from './MobileParts';
import {
  DeviceMark, MoneyText, RankRows, ReadState, ShareStrip, StoreTable, StorefrontGate, StorefrontHead, WeekdayBars, count, currencyName,
  deviceLabel, languageName, primaryMoney, rate, ratio, st, stCount, useStorefrontSite,
} from './StorefrontParts';

// Who visits the store: where they are, on what, in which language, and in
// which market and currency they shop. Places come from where requests reached
// the edge, never from anything a visitor typed; screens and browsers from what
// the browser says about itself.

// A panel's worth of one breakdown: the shares as one bar, then each row with its
// share of the range's sessions.
function Breakdown({ rows, total, icon, label, detail, limit = 6 }) {
  if (rows.length === 0) return <Empty title={st('audience.nothing')} />;
  return (
    <>
      <ShareStrip total={total} parts={rows.map((row) => ({ key: String(row.key), label: label(row), value: row.sessions }))} />
      <RankRows rows={rows.slice(0, limit).map((row) => ({
        key: String(row.key), icon: row.icon || icon, label: label(row), detail: detail ? detail(row) : rate(row.sessions, total),
        value: row.sessions, display: NUMBER.format(row.sessions),
      }))} />
    </>
  );
}

function Places({ data }) {
  const [by, setBy] = useState('countries');
  const countryColumns = [
    { key: 'country', label: st('audience.col_country'), primary: true, sortValue: (row) => countryName(row.code),
      render: (row) => <span className="xo-sf-place-cell"><Flag code={row.code} /><strong>{countryName(row.code)}</strong></span> },
    { key: 'visitors', label: st('audience.col_visitors'), align: 'end', wide: true, sortValue: (row) => row.visitors, render: (row) => count(row.visitors) },
    { key: 'sessions', label: st('audience.col_sessions'), align: 'end', sortValue: (row) => row.sessions, render: (row) => count(row.sessions) },
    { key: 'orders', label: st('audience.col_orders'), align: 'end', sortValue: (row) => row.orders, render: (row) => count(row.orders) },
    { key: 'revenue', label: st('audience.col_revenue'), align: 'end', sortValue: (row) => (primaryMoney(row.revenue) ? primaryMoney(row.revenue).orders : 0),
      render: (row) => <MoneyText list={row.revenue} /> },
  ];
  const cityColumns = [
    { key: 'city', label: st('audience.col_city'), primary: true, sortValue: (row) => row.city,
      render: (row) => <span className="xo-sf-place-cell"><Flag code={row.country} /><strong>{row.city}</strong><small>{countryName(row.country)}</small></span> },
    { key: 'visitors', label: st('audience.col_visitors'), align: 'end', sortValue: (row) => row.visitors, render: (row) => count(row.visitors) },
    { key: 'sessions', label: st('audience.col_sessions'), align: 'end', sortValue: (row) => row.sessions, render: (row) => count(row.sessions) },
  ];
  const regionColumns = [
    { key: 'region', label: st('audience.col_region'), primary: true, sortValue: (row) => row.region,
      render: (row) => <span className="xo-sf-place-cell"><Flag code={row.country} /><strong>{row.region}</strong><small>{countryName(row.country)}</small></span> },
    { key: 'visitors', label: st('audience.col_visitors'), align: 'end', sortValue: (row) => row.visitors, render: (row) => count(row.visitors) },
    { key: 'sessions', label: st('audience.col_sessions'), align: 'end', sortValue: (row) => row.sessions, render: (row) => count(row.sessions) },
  ];
  return (
    <>
      <WorkspaceTabs value={by} onChange={setBy} label={st('audience.places_title')} className="is-compact xo-mob-places-tabs" tabs={[
        { value: 'countries', label: st('audience.tab_countries'), count: data.countries.length },
        { value: 'regions', label: st('audience.tab_regions'), count: data.regions.length },
        { value: 'cities', label: st('audience.tab_cities'), count: data.cities.length },
      ]} />
      {by === 'countries' && <StoreTable columns={countryColumns} rows={data.countries} rowKey={(row) => row.code} limit={8} initialSort={{ key: 'sessions', direction: 'desc' }} empty={<Empty title={st('audience.nothing')} />} />}
      {by === 'regions' && <StoreTable columns={regionColumns} rows={data.regions} rowKey={(row) => `${row.country}|${row.region}`} limit={8} initialSort={{ key: 'sessions', direction: 'desc' }} empty={<Empty title={st('audience.nothing')} />} />}
      {by === 'cities' && <StoreTable columns={cityColumns} rows={data.cities} rowKey={(row) => `${row.country}|${row.city}`} limit={8} initialSort={{ key: 'sessions', direction: 'desc' }} empty={<Empty title={st('audience.nothing')} />} />}
    </>
  );
}

function AudienceView({ site, store }) {
  const [days, setDays] = useState(30);
  const load = useCallback(() => loadStorefrontView('audience', { site: site.code, days }), [site.code, days]);
  const report = useLiveResource(['store', 'store_traffic'], load, { reconcileMs: 120000 });
  const head = <StorefrontHead title={st('audience.title')} subtitle={st('audience.subtitle')} days={days} onDays={setDays} store={store} />;
  if (report.data === null) return <div className="xo-platform-page xo-mobile-page xo-sf-page">{head}<ReadState resource={report} label={st('audience.loading')} /></div>;
  const data = report.data;
  const sessions = data.countries.reduce((sum, row) => sum + row.sessions, 0);
  const phones = data.devices.find((row) => row.deviceType === 'mobile');
  const topLanguage = [...data.languages].sort((a, b) => b.sessions - a.sessions)[0];
  const pings = data.cities.filter((city) => city.lat !== null && city.lng !== null)
    .map((city) => ({ city: city.city, country: city.country, lat: city.lat, lng: city.lng, at: 0, count: city.visitors }));
  return (
    <div className="xo-platform-page xo-mobile-page xo-sf-page">
      {head}
      {report.error && <p className="xo-alert t-warn" role="alert">{report.error}</p>}
      <KpiRow items={[
        { label: st('audience.kpi_countries'), value: count(data.countries.length), detail: stCount('audience.cities_count', data.cities.length) },
        { label: st('audience.kpi_top_country'), value: data.countries[0] ? countryName(data.countries[0].code) : st('none'),
          detail: data.countries[0] ? st('audience.kpi_share', { rate: rate(data.countries[0].sessions, sessions) }) : null },
        { label: st('audience.kpi_phones'), value: phones ? rate(phones.sessions, sessions) : '0%', detail: phones ? st('audience.kpi_phones_detail', { rate: ratio(phones.conversionRate) }) : null },
        { label: st('audience.kpi_language'), value: topLanguage ? languageName(topLanguage.language) : st('none'),
          detail: topLanguage ? st('audience.kpi_share', { rate: rate(topLanguage.sessions, sessions) }) : null },
      ]} />
      <div className="xo-mob-row is-wide-left is-even">
        <Panel title={st('audience.map_title')} detail={st('audience.map_detail')} className="xo-sf-map-fill">
          <PulseMap countries={data.countries} pings={pings} valueKey="visitors" valueLabel={st('kpi.visitors')}
            pingCountKey="ui.storefront.daily_visitors_count" height={400} label={st('audience.map_label')} />
        </Panel>
        <Panel title={st('audience.places_title')} detail={st('audience.places_plain')}>
          <Places data={data} />
        </Panel>
      </div>
      <div className="xo-mob-row is-thirds is-even xo-sf-thirds">
        <Panel title={st('audience.devices_title')} detail={st('audience.devices_detail')}>
          <Breakdown total={sessions} rows={data.devices.map((row) => ({ ...row, key: row.deviceType || 'unknown', icon: <DeviceMark device={row.deviceType} /> }))}
            label={(row) => deviceLabel(row.deviceType)} detail={(row) => st('audience.device_paid', { rate: ratio(row.conversionRate) })} />
        </Panel>
        <Panel title={st('audience.browsers_title')}>
          <Breakdown total={sessions} rows={data.browsers.map((row) => ({ ...row, key: row.browser || 'unknown' }))} icon={<Globe2 aria-hidden="true" />}
            label={(row) => row.browser || st('audience.unknown')} />
        </Panel>
        <Panel title={st('audience.os_title')}>
          <Breakdown total={sessions} rows={data.os.map((row) => ({ ...row, key: row.os || 'unknown' }))} icon={<Monitor aria-hidden="true" />}
            label={(row) => row.os || st('audience.unknown')} />
        </Panel>
      </div>
      <div className="xo-mob-row is-thirds is-even xo-sf-thirds">
        <Panel title={st('audience.languages_title')} detail={st('audience.languages_detail')}>
          <Breakdown total={sessions} rows={data.languages.map((row) => ({ ...row, key: row.language || 'unknown' }))} icon={<Languages aria-hidden="true" />}
            label={(row) => languageName(row.language)} />
        </Panel>
        <Panel title={st('audience.markets_title')} detail={st('audience.markets_detail')}>
          <Breakdown total={sessions} rows={data.markets.map((row) => ({ ...row, key: row.market || 'unknown', icon: row.market ? <Flag code={row.market} /> : <Store aria-hidden="true" /> }))}
            label={(row) => (row.market ? countryName(row.market) : st('audience.unknown'))} />
        </Panel>
        <Panel title={st('audience.currencies_title')} detail={st('audience.currencies_detail')}>
          <Breakdown total={sessions} rows={data.currencies.map((row) => ({ ...row, key: row.currency || 'unknown' }))} icon={<Coins aria-hidden="true" />}
            label={(row) => currencyName(row.currency)} />
        </Panel>
      </div>
      <div className="xo-mob-row is-wide-left is-even">
        <Panel title={st('audience.when_title')} detail={st('audience.when_detail')}>
          <HourHeatmap grid={data.heatmap} countKey="ui.storefront.sessions_count" />
          <WeekdayBars grid={data.heatmap} />
        </Panel>
        <Panel title={st('audience.screens_title')} detail={st('audience.screens_detail')}>
          <Breakdown total={sessions} rows={data.screens.map((row) => ({ ...row, key: `${row.w}x${row.h}` }))} icon={<MonitorSmartphone aria-hidden="true" />}
            label={(row) => `${row.w} × ${row.h}`} limit={8} />
        </Panel>
      </div>
    </div>
  );
}

export default function StorefrontAudience() {
  const store = useStorefrontSite();
  return <StorefrontGate store={store}>{(site) => <AudienceView site={site} store={store} />}</StorefrontGate>;
}
